import { type FileSystem, type Writer } from '@playcanvas/splat-transform';

import { Events } from './events';
import { writeSplatFile, type SerializeSettings } from './splat-serialize';

/**
 * Scan-the-Wild save-back: return an edited splat to the pipeline as a new
 * deliverable revision, instead of downloading it.
 *
 * When the editor is opened from the admin console it carries a run context in
 * the URL fragment (kept out of query strings/logs):
 *   #run=<sessionId>/<runId>&api=<apiBase>&pin=<adminPin>&source=<final|logo-reposition>
 *
 * On "Save revision" we serialize the current scene TWICE from the one edited
 * set of splats — the full `.ply` (the guest download) and the compressed
 * `.sog` (the viewer) — so the two deliverables can't drift, upload both to the
 * run's S3 prefix via presigned PUTs, then record them via /revise. The world
 * transform (reorientation) and SH rotation are baked by the serializer.
 *
 * Everything is gated by the admin PIN for now, like the rest of the console.
 */

type RunContext = {
    apiBase: string;
    sessionId: string;
    runId: string;
    pin: string;
    source: string;
};

// Parse the run context from the URL fragment. Null when the editor was opened
// standalone (no save-back target) — then we leave the stock Publish flow.
const readRunContext = (): RunContext | null => {
    const frag = new URLSearchParams(location.hash.replace(/^#/, ''));
    const run = frag.get('run');
    const apiBase = frag.get('api');
    const pin = frag.get('pin');
    if (!run || !apiBase || !pin) {
        return null;
    }
    const [sessionId, runId] = run.split('/');
    if (!sessionId || !runId) {
        return null;
    }
    return { apiBase, sessionId, runId, pin, source: frag.get('source') ?? 'final' };
};

// Writer that accumulates the serialized bytes in memory (as chunks, to avoid
// one giant contiguous copy) so we can PUT the whole file to a presigned S3 URL.
class CollectingWriter implements Writer {
    private chunks: Uint8Array[] = [];
    private cursor = 0;

    get bytesWritten(): number {
        return this.cursor;
    }

    write(data: Uint8Array): Promise<void> {
        // The serializer reuses its scratch buffer between writes — copy.
        this.chunks.push(data.slice());
        this.cursor += data.byteLength;
        return Promise.resolve();
    }

    close(): Promise<void> {
        return Promise.resolve();
    }

    abort(): Promise<void> {
        return Promise.resolve();
    }

    blob(): Blob {
        // No blob type -> fetch sends NO Content-Type. The presigned PUT is
        // signed with an empty content-type (boto3 default), so sending any
        // Content-Type makes S3 compute a different SigV2 signature -> 403.
        return new Blob(this.chunks as BlobPart[]);
    }
}

// FileSystem that funnels every file the serializer opens into one collecting
// writer. Both our serializers (.ply, .sog) write a single file, mirroring how
// file-handler serializes to a single BrowserFileSystem stream.
class CollectingFileSystem implements FileSystem {
    writer = new CollectingWriter();

    createWriter(_filename: string): Writer {
        return this.writer;
    }

    mkdir(_path: string): Promise<void> {
        return Promise.resolve();
    }
}

// Serialize the current scene to .ply bytes. (SOG compression is done by a GPU
// worker after save, never in the browser — see scene.saveRevision.)
const serialize = async (
    events: Events,
    _format: 'ply',
): Promise<Blob> => {
    const splats = events.invoke('scene.splats');
    const settings: SerializeSettings = { maxSHBands: 3 };
    const fs = new CollectingFileSystem();
    await writeSplatFile(splats, settings, 'ply', 'splat.ply', {}, fs);
    return fs.writer.blob();
};

// POST/GET our pipeline API with the admin PIN.
const api = async (ctx: RunContext, path: string, body?: unknown) => {
    const res = await fetch(`${ctx.apiBase}${path}`, {
        method: body === undefined ? 'GET' : 'POST',
        headers: {
            Authorization: `Bearer ${ctx.pin}`,
            ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!res.ok) {
        throw new Error(`${path} failed (${res.status})`);
    }
    return res.json();
};

// Mint a presigned PUT for `filename` under the run's prefix, upload the blob,
// and return the S3 key the pipeline recorded.
const upload = async (ctx: RunContext, filename: string, blob: Blob): Promise<string> => {
    const { session_id: _sid, s3_key, upload_url } = await api(
        ctx,
        `/api/pipeline-runs/${ctx.sessionId}/${ctx.runId}/output-url`,
        { filename },
    );
    // No Content-Type header — the presigned PUT is signed with an empty one
    // (see CollectingWriter.blob); any Content-Type breaks the SigV2 signature.
    const put = await fetch(upload_url, {
        method: 'PUT',
        body: blob,
    });
    if (!put.ok) {
        throw new Error(`upload of ${filename} failed (${put.status})`);
    }
    return s3_key;
};

const registerSaveRevisionEvents = (events: Events) => {
    // True when the editor was opened with a pipeline run context — the menu
    // item is only shown/enabled then.
    events.function('revision.enabled', () => readRunContext() !== null);

    events.function('scene.saveRevision', async () => {
        const ctx = readRunContext();
        if (!ctx) {
            return;
        }
        try {
            events.fire('progressStart', 'Saving edit...');

            const stamp = (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}`).slice(0, 8);
            // Export ONLY the .ply (a plain write — fast). The logo merge +
            // .sog compression run on a GPU worker, not here: encoding SOG
            // inline in the browser freezes the tab for minutes on large splats.
            events.fire('progressUpdate', { text: 'Exporting splat...', progress: 20 });
            const plyBlob = await serialize(events, 'ply');

            events.fire('progressUpdate', { text: 'Uploading...', progress: 60 });
            const rawKey = await upload(ctx, `rev-${stamp}-raw.ply`, plyBlob);

            events.fire('progressUpdate', { text: 'Queuing re-finish...', progress: 95 });
            await api(ctx, `/api/pipeline-runs/${ctx.sessionId}/${ctx.runId}/refinish`, {
                raw_s3_key: rawKey,
                source: ctx.source,
            });

            await events.invoke('showPopup', {
                type: 'info',
                header: 'Edit saved',
                message: 'A worker is re-finishing your splat (logo + compression). The updated version will appear on the run shortly — you can close this tab.',
            });
        } catch (error) {
            await events.invoke('showPopup', {
                type: 'error',
                header: 'Save failed',
                message: `${(error as Error).message ?? error}`,
            });
        } finally {
            events.fire('progressEnd');
        }
    });
};

export { registerSaveRevisionEvents };
